/**
 * Правовые документы AiSearch.
 *
 * Тексты перенесены с работающего сервиса aisearch.ru: сочинять свои
 * формулировки для соглашения и оферты нельзя, это документы, на
 * которые ссылается оплата и на которые смотрит проверяющий.
 *
 * Хранятся как markdown-строки в коде, а не в `messages/*.json`, по
 * двум причинам. Первая: это один документ на языке права, а не набор
 * подписей интерфейса, и дробить его на сотню ключей — значит
 * гарантированно рассинхронизировать абзацы при первой же правке.
 * Вторая: документ существует только по-русски и относится к
 * законодательству Российской Федерации; машинный перевод оферты хуже,
 * чем её отсутствие, потому что выглядит как обязательство.
 *
 * Поэтому на других языках показывается тот же русский текст с явной
 * пометкой, что юридически значима русская редакция.
 */

export interface LegalDoc {
  /** Часть адреса: /legal/<slug>. */
  slug: string;
  /** Заголовок документа, он же заголовок вкладки. */
  title: string;
  /** Короткое описание для метаданных и списка. */
  description: string;
  /** Дата последнего обновления на исходном сервисе. */
  updated: string;
  /** Тело документа в markdown. */
  body: string;
  /**
   * Черновик: страница под noindex, вне подвала и карты сайта, пока
   * владелец не одобрит текст. Открывается только по прямой ссылке —
   * например, из галочки согласия.
   */
  chernovik?: boolean;
}

/**
 * Склеивает строки одного абзаца.
 *
 * Тексты набраны с переносами по ширине, чтобы их можно было читать и
 * править в исходнике. Но рендерер Markdown считает абзацем строку, а не
 * блок между пустыми строками, и документ выходил лесенкой из коротких
 * абзацев — ровно так же выглядел бы неотформатированный договор.
 *
 * Строки заголовков, списков и пустые строки остаются как есть: их
 * перенос значим.
 */
function unwrap(source: string): string {
  const out: string[] = [];

  for (const line of source.split('\n')) {
    const special = line.trim() === '' || /^(#{1,6}\s|[-*+]\s|>\s|\d+[.)]\s|\|)/.test(line.trim());
    const previous = out[out.length - 1];
    const canJoin =
      !special &&
      previous !== undefined &&
      previous.trim() !== '' &&
      !/^(#{1,6}\s|[-*+]\s|>\s|\d+[.)]\s|\|)/.test(previous.trim());

    if (canJoin) {
      out[out.length - 1] = `${previous} ${line.trim()}`;
      continue;
    }

    out.push(line);
  }

  return out.join('\n');
}

const USER_AGREEMENT = `
Добро пожаловать в сервис AiSearch, который предоставляет доступ к
генеративным нейросетям. Для использования наших услуг необходимо
ознакомиться с пользовательским соглашением. Регистрируясь в сервисе, вы
соглашаетесь с нашими политиками и правилами использования услуги.

## Услуги, предоставляемые сервисом

Мы предоставляем услугу генерации контента с помощью нейронных сетей
сторонних поставщиков, включая модели OpenAI, Google, Anthropic, BytePlus и
других правообладателей. Какие данные при этом передаются за рубеж и как
они защищаются, описано в
[Политике конфиденциальности](/legal/privacy#za-rubezh). Сервис использует компьютерные алгоритмы и базы данных
для обработки текста, изображений и звука.

Используя сервис, вы соглашаетесь также с правилами использования тех
моделей, к которым обращаетесь через сервис.

Мы предоставляем доступ к нейросети и не несём ответственности за
содержание и достоверность ответа нейросети.

## Конфиденциальность

Мы обеспечиваем защиту конфиденциальности информации пользователей. Мы не
раскрываем личные данные без предварительного согласия пользователя или
вне оснований, предусмотренных законом.

## Авторские права

Мы не претендуем на авторские права на материалы, которые вы обрабатываете
через сервис. Мы не сохраняем обрабатываемые материалы без явного согласия
пользователя.

## Условия использования услуги

Мы не несём ответственности за использование услуг с нарушением
законодательства или правил публичной морали. Пользователи обязаны
использовать услуги в соответствии с законодательством.

## Отказ от ответственности

Мы не несём ответственности за ущерб, причинённый пользователями, включая
ущерб, причинённый использованием услуги.

## Требования к содержанию

В сервисе запрещено создавать и публиковать информацию, которая: призывает
к противозаконным действиям или потенциально опасной деятельности;
нарушает права пользователей; нарушает требования законодательства;
способствует деятельности, которая может повлечь угрозу жизни и здоровью
людей; является пропагандой запрещённых законодательством Российской
Федерации сведений.

## Синхронизация губ и изображения людей {#lip-sync}

Инструмент «Синхронизация губ» изменяет движение губ человека на видео
под загруженную речь. Используя его, пользователь подтверждает, что на
видео изображён он сам либо человек, который дал согласие на такую
обработку своего изображения (ст. 152.1 Гражданского кодекса РФ).

Запрещается:

- создавать ролики с изображением публичных лиц, политиков и должностных лиц;
- выдавать созданный ролик за подлинную запись слов или действий реального человека;
- использовать ролики для обмана, мошенничества, давления, травли, вмешательства в выборы, а также для создания материалов сексуального характера;
- удалять, закрывать или иным способом скрывать метку «Создано ИИ».

Каждый созданный ролик содержит неудаляемую метку «Создано ИИ».
Ролики публикуются в общем каталоге сервиса только после проверки
модератором. При нарушении этих правил сервис вправе удалить ролик, заблокировать
учётную запись и передать сведения по запросу уполномоченных органов.
Средства за услуги, оказанные до блокировки, не возвращаются (раздел 6
Договора оферты). Ответственность за содержание ролика
и наличие согласия изображённого человека несёт пользователь.

## Изменения в соглашении

Мы оставляем за собой право изменять условия пользовательского соглашения.
Действующая редакция всегда размещена на этой странице.
`.trim();

const PRIVACY = `
Все собранные данные пользователя защищены в соответствии с
законодательством Российской Федерации «О персональных данных».

## Согласие на обработку персональных данных

При регистрации сервис AiSearch запрашивает информацию для идентификации.
В случае отказа предоставить необходимые данные сервис не несёт
ответственности за возможное ограничение использования.

Если информация не является важной для основной работы сервиса,
пользователь может её не предоставлять без негативных последствий.
Примеры такой информации: файлы cookie для маркетинга и название компании.

Соглашаясь с настоящей Политикой и предоставляя персональные данные,
пользователь выражает согласие на их обработку в соответствии с
положениями настоящей Политики.

## Сбор данных

Сервис может собирать информацию, позволяющую идентифицировать
пользователя: имя, регион использования, адрес электронной почты,
платёжные реквизиты. Дополнительно собираются обезличенные данные об
использовании сервиса с помощью файлов cookie.

## Файлы cookie {#cookie}

Файлы cookie — небольшие текстовые файлы, которые сайт сохраняет в
браузере. Ниже перечислено, какие из них используются и зачем.

Сервис устанавливает свои файлы cookie:

- \`NEXT_LOCALE\` — выбранный язык интерфейса;
- файлы cookie сессии и авторизации — вход в аккаунт и сохранение работы
  между страницами;
- \`aisearch_cookie_notice\` — отметка о том, что уведомление о файлах
  cookie закрыто, чтобы не показывать его повторно.

Сервис использует Яндекс.Метрику (счётчик 92301734), которая
устанавливает свои файлы cookie: \`_ym_uid\`, \`_ym_d\`, \`_ym_isad\`. Их
назначение — подсчёт посещений и понимание того, как посетители
пользуются сайтом. Собираемые таким образом сведения обезличены.

Отказаться от файлов cookie можно двумя способами: изменить настройки
браузера, запретив их сохранение, либо установить
[дополнение для отключения Яндекс.Метрики](https://yandex.ru/support/metrica/general/opt-out.html).

При отключении файлов cookie перестанут работать вход в аккаунт и
сохранение проектов: сервис не сможет узнать посетителя между
страницами.

## Использование данных

Данные используются только для предоставления услуг, обработки платежей и
отправки информации о заказах. Новости и специальные предложения на
электронную почту отправляются только тем, кто дал на это отдельное
согласие: условия описаны в документе
[«Согласие на получение информационной и рекламной рассылки»](/legal/soglasie-na-rassylku).
Отозвать его можно в любой момент. Для улучшения качества услуг
обрабатываются обезличенные данные. По запросу судебных или
правоохранительных органов информация может быть предоставлена без
согласия пользователя. Иным третьим лицам доступ к персональным данным без
согласия пользователя не предоставляется.

## Передача данных за рубеж {#za-rubezh}

Часть инструментов работает с помощью нейросетей зарубежных поставщиков.
Чтобы получить результат, сервис передаёт поставщику содержимое запроса:

- **Чат и текстовые инструменты** — запрос проходит через сервер AiSearch в Германии и передаётся поставщику выбранной модели: OpenAI, Google, Anthropic и другим, в том числе через OpenRouter (США и другие страны). Передаются текст запроса и приложенные файлы.
- **Создание картинок** — Cloudflare (США) и поставщики моделей через OpenRouter: текст описания, а при правке — исходная картинка.
- **Видео из текста** — BytePlus Pte. Ltd. (Сингапур), модель Seedance: текст описания сцены и, если выбран начальный кадр, картинка, созданная в сервисе AiSearch. Собственные фотографии и фотографии людей для создания видео не принимаются.

Перед отправкой в чат текст запроса проверяет наша модель поиска
персональных данных, работающая в России. Телефоны, адреса, адреса
электронной почты, номера документов и другие персональные данные
заменяются условными метками, а в ответе модели подставляются обратно.
Имена и названия мест заменяются, когда рядом с ними есть такие данные.
Если модель проверки недоступна, запрос за рубеж не отправляется.
Приложенные изображения передаются как есть.

Имя, адрес электронной почты, платёжные данные и IP-адрес пользователя
поставщикам не передаются: запрос отправляет сервер AiSearch от своего
имени. Цель передачи — выполнить запрос пользователя и вернуть ему
результат.

Автоматическая проверка не гарантирует, что найдено всё. Не указывайте в
запросах персональные данные — свои и других людей: имена вместе с
телефонами и адресами, номера документов, сведения о здоровье.

## Видео и голос пользователей {#video-golos}

Для инструментов, которые работают с видео и звуком (синхронизация губ,
шумоподавление, минус из песни, распознавание речи), сервис получает
загруженные файлы. На видео может быть изображён человек, а в записи —
его голос: это персональные данные. Такие файлы обрабатываются только на
серверах сервиса в России, за рубеж не передаются и не используются для
обучения нейросетей. Изображение и голос не используются для
установления личности человека.

Сроки хранения. Исходный файл, загруженный для минуса из песни или
шумоподавления, удаляется после обработки; загрузка, по которой
обработка не запускалась, — через 3 часа. Результат хранится во
временном хранилище 1 час (у вошедшего пользователя, которому не удалось
сохранить его в «Файлы», — 24 часа). Файлы, которые пользователь
сохранил в разделе «Файлы» или загрузил туда для синхронизации губ, —
исходное видео, запись речи и готовый ролик — хранятся, пока
пользователь их не удалит.

Загружая видео или запись с другим человеком, пользователь подтверждает,
что этот человек дал согласие на такую обработку.

## Хранение информации

Данные хранятся только в течение периода использования сервиса. Если
пользователь удалил учётную запись, срок подписки окончился или поступил
запрос на удаление данных, они удаляются в течение 14 рабочих дней, за
исключением случаев, когда хранение требуется по закону.

## Права пользователей

- **Право доступа**: получение копии своих персональных данных.
- **Право на исправление**: исправление недостоверных данных.
- **Право на удаление**: запрос на удаление данных или их части.
- **Право на ограничение обработки**: если данные указаны неверно, не нужны
  или пользователь возражает против обработки.
- **Право на переносимость**: передача своих данных третьей стороне.

Для использования этих прав напишите на [help@aisearch.tech](mailto:help@aisearch.tech).

## Безопасность хранения

Применяются защитное программное обеспечение, защищённые соединения TLS
между серверами и устройствами пользователей, а также стандарты DKIM, SPF
и DMARC для защиты почтового трафика.

## Изменения в Политике

Сервис вправе изменить Политику конфиденциальности. О существенных
изменениях пользователи уведомляются за две недели до вступления в силу.
`.trim();

const OFFER = `
Настоящий документ представляет собой договор оферты, размещённый для
ознакомления неограниченного круга лиц с целью предоставления Лицензиату
на условиях простой (неисключительной) лицензии права использования
программ для ЭВМ и баз данных. В соответствии со ст. 435, 437 Гражданского
кодекса РФ настоящий документ является публичной офертой.

## 1. Термины и определения

**Лицензиар** — Индивидуальный предприниматель Фролов Валерий Николаевич,
ИНН 502980825501, ОГРНИП 323508100278546 от 24.05.2023.

**Лицензиат** — лицо, заключившее с Лицензиаром договор на условиях
настоящей Оферты.

**Сайт** — программное решение, доступ к которому осуществляется через
интернет по доменному имени https://aisearch.tech/, включая все разделы и
уровни этого домена.

**Лицензия** — право Лицензиата использовать программное обеспечение и базы
данных Лицензиара посредством Сайта.

**Личный кабинет** — программа с индивидуальным доступом для авторизации
Лицензиата и учёта введённых им данных.

**Пакеты** — предоставление Лицензиату за лицензионное вознаграждение
доступа к функциональным возможностям Сайта в течение ограниченного
периода.

## 2. Предмет соглашения

2.1. Настоящий договор определяет условия предоставления Лицензиату права
использования программ для ЭВМ и баз данных, расположенных на Сайте.

2.2. Право предоставляется на территории любого государства на срок
действия настоящего Соглашения.

2.3. Лицензиар сохраняет за собой право выдачи лицензий другим лицам.

2.4. Лицензиату не предоставляются права на следующие действия: обходить
меры технологической защиты Сайта; взламывать, декомпилировать,
расшифровывать, эмулировать или выполнять обратное проектирование Сайта;
копировать, сдавать в аренду, публиковать, продавать, распространять Сайт
и его производные; использовать Сайт способом, мешающим другим
пользователям.

2.5. Безусловным принятием (акцептом) условий считается оплата
лицензионного вознаграждения либо использование Сайта в рамках
демонстрационной лицензии.

2.6. Лицензиар не гарантирует, что использование функций Сайта предоставит
полностью верную и корректную информацию по запросам Лицензиата.

## 3. Права и обязанности

3.1. Лицензиар обязуется предоставить фактическую возможность пользоваться
функциональными возможностями Сайта в согласованном объёме и не
предоставлять другим лицам доступ к личному кабинету Лицензиата.

3.2. Лицензиар вправе модифицировать программы и базы данных при условии
сохранения данных Лицензиата, изменять условия Соглашения, а также
приостановить лицензию при нарушении Лицензиатом условий Соглашения или
законодательства.

3.3. Лицензиат обязуется извещать Лицензиара о неполадках в доступе,
выплачивать лицензионное вознаграждение, не копировать и не распространять
Сайт и его производные, не размещать заведомо недостоверную информацию.

3.4. Лицензиату запрещается получать несанкционированный доступ к функциям
Сайта, получать информацию о других Лицензиатах, использовать Сайт в
целях, противоречащих законодательству Российской Федерации, а также
передавать свои учётные данные третьим лицам.

## 4. Вознаграждение и порядок оплаты

4.1. Актуальная информация о размере вознаграждения размещена в общем
доступе на странице тарифов.

4.2. Вознаграждение выплачивается безналичным расчётом, в том числе через
финансовых агентов Лицензиара, в порядке предоплаты.

4.3. Лицензиат понимает и соглашается, что пакет имеет свой срок действия
и лимит, и обязан самостоятельно отслеживать состояние пакета.

## 5. Ответственность

5.1. При нарушении доступности Сайта по вине Лицензиара предоставление
лицензии продлевается на срок такого нарушения.

5.2. Лицензиар не несёт ответственности за достоверность данных, вводимых
Лицензиатом, и за прямой или косвенный ущерб, возникший не по вине
Лицензиара.

5.3. При нарушении Лицензиатом условий Соглашения Лицензиар вправе в
одностороннем внесудебном порядке отказаться от исполнения Соглашения без
возврата лицензионного вознаграждения.

## 6. Возврат средств и прекращение действия

6.1. Денежные средства за предыдущие расчётные периоды не возвращаются:
услуги за них уже оказаны надлежащим образом независимо от того,
пользовался ли Лицензиат Сайтом (условия абонентского договора, ст. 429.4
ГК РФ).

6.2. Основанием для возврата не является отказ Лицензиата от использования
услуг до истечения срока оплаченного доступа.

6.3. Лицензиар вправе добровольно вернуть средства, если списание
произошло в результате мошеннических действий третьих лиц при отсутствии
вины Лицензиара.

## 7. Заключительные положения

7.1. Все споры решаются в претензионном порядке: сторона направляет другой
стороне сообщение по электронной почте.

7.2. Если ответ не получен в течение 30 дней либо стороны не пришли к
соглашению, спор разрешается в судебном порядке по месту нахождения
Лицензиара.

7.3. Вопросы, не урегулированные настоящим договором, разрешаются в
соответствии с законодательством Российской Федерации.
`.trim();

/**
 * Оферта на лицензию программы (#242). Отдельный договор от оферты на
 * сервис: здесь продаётся право пользоваться программой на компьютере
 * человека, а не доступ к сервису.
 *
 * Решения владельца (01.10.2026): два вида — «на 3 года обновлений» и
 * «навсегда», срок — это срок обновлений, а не работы программы; одна
 * лицензия — одно рабочее место, перенос на другое устройство разрешён,
 * технически не контролируется. Возврат 14 дней, поддержка ai@aisearch.ru.
 * Редакция совпадает с OfferVersion в API: она пишется в каждый заказ.
 */
const LICENSE_OFFER = `
Редакция от 01.10.2026.

Настоящий документ является публичной офертой (ст. 435, 437 ГК РФ)
индивидуального предпринимателя Фролова Валерия Николаевича заключить
лицензионный договор о предоставлении права использования программы для ЭВМ
на изложенных ниже условиях. Договор заключается путём присоединения
(п. 5 ст. 1286 ГК РФ), без подписания бумажного экземпляра.

## 1. Термины

**Лицензиар** — индивидуальный предприниматель Фролов Валерий Николаевич,
ИНН 502980825501, ОГРНИП 323508100278546 от 24.05.2023.

**Лицензиат** — физическое лицо, индивидуальный предприниматель или
организация, оплатившие Лицензию.

**Программа** — программа для ЭВМ, указанная на Странице покупки (например,
«Конспект»).

**Ключ** — строка символов, выданная после оплаты, которая подтверждает
Лицензию и снимает в Программе ограничения пробного режима.

**Рабочее место** — одно устройство (компьютер), на котором Программа
используется с Ключом в данный момент.

**Страница покупки** — страница сайта https://aisearch.ru/pricing/license/…,
на которой указаны Программа, вид Лицензии и цена.

## 2. Предмет

2.1. Лицензиар предоставляет Лицензиату право использования Программы на
условиях простой (неисключительной) лицензии, а Лицензиат выплачивает
вознаграждение.

2.2. Способы использования: установка и запуск на устройстве Лицензиата,
воспроизведение в памяти устройства в объёме, необходимом для работы
Программы, использование всех её функций.

2.3. Одна Лицензия даёт право пользоваться Программой на одном Рабочем
месте. Лицензиат вправе переносить Программу с Ключом на другое своё
устройство (например, при замене компьютера), но не вправе использовать
один Ключ одновременно на нескольких устройствах. Программа этого технически
не ограничивает: Лицензиат обязуется соблюдать это условие сам. Для
нескольких рабочих мест приобретается соответствующее число Лицензий.

2.4. Территория: весь мир.

2.5. Срок Лицензии: на весь срок действия исключительного права на
Программу. Право пользоваться полученными версиями Программы по истечении
какого-либо срока не прекращается.

2.6. Обновления Программы зависят от вида Лицензии, указанного на Странице
покупки и записанного в Ключ:

- **«Навсегда»** — Ключ действует для всех версий Программы, в том числе
  выпущенных в будущем;
- **«На 3 года обновлений»** — Ключ действует для всех версий Программы,
  выпущенных до последнего дня трёхлетнего срока со дня выдачи Ключа
  включительно. Версии, выпущенные позже, с этим Ключом работают в пробном
  режиме; Лицензиат вправе продолжать пользоваться любой версией, выпущенной
  в пределах срока.

2.7. Исходный код Программы может быть опубликован Лицензиаром на условиях
отдельной открытой лицензии. Настоящий договор не ограничивает прав, которые
Лицензиат имеет по такой открытой лицензии, и предоставляет право
использовать готовую сборку Программы с Ключом.

2.8. Лицензиат не вправе передавать, продавать или публиковать Ключ. В Ключ
записаны имя или наименование Лицензиата и его адрес электронной почты.

## 3. Заключение договора

3.1. Акцептом является оплата Лицензии на Странице покупки (п. 3 ст. 438
ГК РФ). Перед оплатой Лицензиат подтверждает согласие с Офертой отметкой в
форме.

3.2. Договор считается заключённым в момент поступления оплаты Лицензиару.

3.3. Лицензиат гарантирует, что указанные им имя (наименование) и адрес
электронной почты достоверны: они записываются в Ключ и используются для его
отправки.

## 4. Вознаграждение и оплата

4.1. Размер вознаграждения указан на Странице покупки в рублях. Лицензиар
применяет упрощённую систему налогообложения, НДС не облагается.

4.2. Оплата производится через платёжный сервис ЮKassa. Кассовый чек
(54-ФЗ) направляется на адрес электронной почты Лицензиата.

4.3. Вознаграждение уплачивается единовременно.

## 5. Передача Ключа

5.1. Ключ показывается на странице заказа сразу после подтверждения оплаты
и направляется на адрес электронной почты Лицензиата. Лицензия считается
предоставленной с момента показа Ключа на странице заказа.

5.2. Утерянный Ключ можно бесплатно получить повторно на странице «Потерял
ключ», указав адрес электронной почты, использованный при покупке.

5.3. Программа проверяет Ключ на устройстве Лицензиата без обращения к
серверам Лицензиара. Для активации подключение к интернету не требуется.

## 6. Возврат вознаграждения

6.1. Лицензиат вправе в течение 14 календарных дней с даты оплаты
отказаться от договора и получить вознаграждение обратно, написав на адрес
ai@aisearch.ru с того же адреса электронной почты и указав номер лицензии.
Причину указывать не обязательно.

6.2. Возврат производится тем же способом, которым была произведена оплата,
в срок до 10 рабочих дней с даты обращения.

6.3. После возврата Лицензия прекращается, Ключ считается отозванным, и
Лицензиат обязан прекратить использование Программы с этим Ключом.

6.4. Если Программа имеет недостаток, препятствующий её использованию, и
Лицензиар не устранил его в разумный срок после обращения Лицензиата,
вознаграждение возвращается независимо от срока, указанного в п. 6.1.

## 7. Гарантии и ответственность

7.1. Лицензиар гарантирует, что обладает правами, достаточными для
предоставления Лицензии.

7.2. Программа предоставляется «как есть». Лицензиар не гарантирует, что
Программа подходит для конкретной цели Лицензиата и работает без ошибок на
любом оборудовании. До покупки Лицензиат может проверить Программу в пробном
режиме.

7.3. Ответственность Лицензиара по договору ограничена суммой уплаченного
вознаграждения, за исключением случаев, когда такое ограничение не
допускается законом.

7.4. Программа обрабатывает записи и тексты на устройстве Лицензиата.
Лицензиар не получает их содержание и не отвечает за их сохранность.

## 8. Персональные данные

8.1. Для заключения и исполнения договора Лицензиар обрабатывает адрес
электронной почты и имя (наименование) Лицензиата: они нужны, чтобы
выпустить Ключ, отправить его и чек, повторно выслать Ключ по запросу.

8.2. Данные хранятся на серверах в Российской Федерации. Обработка ведётся в
соответствии с Политикой конфиденциальности
(https://aisearch.ru/legal/privacy) и отдельным согласием на обработку
персональных данных (https://aisearch.ru/legal/license-consent), которое
Лицензиат даёт при оформлении покупки.

8.3. По запросу Лицензиата его данные удаляются, кроме сведений о покупке,
которые Лицензиар обязан хранить по налоговому и бухгалтерскому
законодательству.

## 9. Прочее

9.1. Лицензиар вправе изменять Оферту. Изменения не затрагивают уже
заключённые договоры: к ним применяется редакция, действовавшая в момент
оплаты.

9.2. Споры решаются путём переговоров, претензионный срок — 30 дней. При
недостижении согласия спор передаётся в суд по месту нахождения Лицензиара,
а если Лицензиат — потребитель, по правилам подсудности Закона о защите
прав потребителей.

9.3. Контакты: ai@aisearch.ru.
`.trim();

/**
 * Согласие на обработку персональных данных при покупке лицензии. С
 * 01.09.2025 оформляется отдельно от других документов (ч. 1 ст. 9 152-ФЗ),
 * поэтому в форме своя галочка.
 */
const LICENSE_CONSENT = `
Я даю согласие ИП Фролову Валерию Николаевичу (ИНН 502980825501) на
обработку моих персональных данных: адреса электронной почты и имени
(наименования), указанных в форме покупки лицензии.

**Цель:** заключение и исполнение лицензионного договора, выпуск и отправка
ключа лицензии, отправка кассового чека, повторная отправка ключа по моему
запросу.

**Действия:** сбор, запись, хранение, использование, передача платёжному
сервису ЮKassa для оплаты и оператору фискальных данных для чека, удаление.
Обработка ведётся с использованием средств автоматизации, данные хранятся в
Российской Федерации.

Согласие действует до его отзыва; отозвать его можно письмом на
ai@aisearch.ru. После отзыва сведения о покупке хранятся в объёме и в
сроки, требуемые налоговым и бухгалтерским законодательством.
`.trim();

/**
 * Согласие на информационную и рекламную рассылку (38-ФЗ ст. 18).
 *
 * Отдельный документ, а не пункт политики: согласие на рекламу должно
 * быть самостоятельным и необязательным, и галочка в форме регистрации
 * ссылается именно на него. Согласие только на рассылку AiSearch
 * (aisearch.ru): решение владельца 06.10.2026 — продукты не смешивать, у
 * каждого сайта своё согласие и своя отписка. Конспект — программа для
 * Windows, aisearch.ru для неё только витрина, и в это согласие она не
 * входит.
 *
 * Одобрен владельцем 06.10.2026, индексируется. Редакция
 * совпадает с `SOGLASIE_VERSIYA` в `lib/soglasie-rassylki.ts` — правка
 * текста меняет и её.
 */
const SOGLASIE_NA_RASSYLKU = `
Редакция v2-2026-10.

Ставя отметку «Хочу получать новости и специальные предложения на почту»
при регистрации, в личном кабинете или в предложении в кабинете, я даю
согласие на получение информационной и рекламной рассылки на условиях
ниже (ч. 1 ст. 18 Федерального закона «О рекламе»).

## 1. Кто отправляет

Рассылку ведёт ИП Фролов Валерий Николаевич (ИНН 502980825501) по сервису
AiSearch (aisearch.ru).

## 2. Что будет приходить

- новости сервиса: новые возможности, изменения, обновления;
- специальные предложения: скидки, акции, условия тарифов и лицензий.

Письма приходят только от AiSearch. Реклама третьих лиц в
рассылке не размещается, и адрес электронной почты для неё никому не
передаётся.

## 3. Канал

Электронная почта на адрес, указанный в учётной записи.

## 4. Какие данные используются

Адрес электронной почты и имя, если оно указано, — чтобы отправить письмо
и обратиться по имени. Вместе с решением сохраняются дата, место, где оно
принято (регистрация, кабинет), сайт и редакция этого текста.

Данные пользователей aisearch.ru хранятся на серверах в Российской
Федерации и обрабатываются в соответствии с
[Политикой конфиденциальности](/legal/privacy).

## 5. Добровольность

Согласие необязательно. Регистрация и пользование сервисами от него не
зависят, без согласия приходят только служебные письма: подтверждение
почты, восстановление пароля, сведения об оплате и заказах.

## 6. Срок и отзыв

Согласие действует до отзыва. Отозвать его можно в любой момент и
бесплатно:

- по ссылке «Отписаться» в любом письме рассылки;
- в настройках личного кабинета;
- письмом на ai@aisearch.ru.
`.trim();

export const AIS_LEGAL_DOCS: LegalDoc[] = [
  {
    slug: 'user-agreement',
    title: 'Пользовательское соглашение',
    description:
      'Условия использования сервиса AiSearch: услуги, конфиденциальность, авторские права и ограничения.',
    updated: '2026-10-27',
    body: unwrap(USER_AGREEMENT),
  },
  {
    slug: 'privacy',
    title: 'Политика конфиденциальности',
    description:
      'Как AiSearch собирает, использует и хранит персональные данные, и какие права есть у пользователя.',
    updated: '2026-10-27',
    body: unwrap(PRIVACY),
  },
  {
    slug: 'offer',
    title: 'Договор оферты',
    description:
      'Публичная оферта на предоставление простой (неисключительной) лицензии на использование сервиса AiSearch.',
    updated: '2026-05-28',
    body: unwrap(OFFER),
  },
  {
    slug: 'license-offer',
    title: 'Оферта на лицензию программы',
    description:
      'Публичная оферта на простую (неисключительную) лицензию на программу: виды лицензии, рабочее место, обновления, возврат.',
    updated: '2026-10-01',
    body: unwrap(LICENSE_OFFER),
  },
  {
    slug: 'license-consent',
    title: 'Согласие на обработку персональных данных при покупке лицензии',
    description: 'Какие данные покупателя лицензии обрабатываются, зачем и как отозвать согласие.',
    updated: '2026-10-01',
    body: unwrap(LICENSE_CONSENT),
  },
  {
    slug: 'soglasie-na-rassylku',
    title: 'Согласие на получение информационной и рекламной рассылки',
    description:
      'Кто присылает новости и специальные предложения AiSearch, какие данные нужны и как отписаться от рассылки.',
    updated: '2026-10-06',
    body: unwrap(SOGLASIE_NA_RASSYLKU),
    chernovik: false,
  },
];

/** Документ по адресу. */
export function legalDoc(slug: string): LegalDoc | undefined {
  return AIS_LEGAL_DOCS.find((doc) => doc.slug === slug);
}
